// Direct-to-GCS image uploads via server-issued signed URLs.
// Image bytes go straight from the browser to the bucket, bypassing the API
// gateway's request-size limit. The server only issues short-lived signed URLs
// and later validates/finalizes the resulting object keys when the report or
// cleanup is submitted.

import { API_URL } from "../utils/api";
import { prepareImageForDirectUpload } from "../utils/uploadImages";

interface SignedUpload {
  key: string;
  url: string;
  contentType: string;
}

// Normalize to a canonical image MIME the server accepts (e.g. the
// non-standard "image/jpg" -> "image/jpeg"). Passthrough PNG/WebP are kept.
const normalizeContentType = (type?: string): string => {
  const value = (type || "").toLowerCase();
  if (value === "image/png") return "image/png";
  if (value === "image/webp") return "image/webp";
  return "image/jpeg";
};

// Limit concurrent PUTs so a big batch doesn't saturate the connection.
const MAX_CONCURRENT_UPLOADS = 3;

const readJsonError = async (response: Response): Promise<string> => {
  try {
    const data = await response.json();
    return data?.error || data?.message || `HTTP ${response.status}`;
  } catch {
    return `HTTP ${response.status}`;
  }
};

/** Ask the server for one signed upload URL per file (by content type). */
export const requestUploadUrls = async (
  files: { contentType: string }[],
  token: string
): Promise<SignedUpload[]> => {
  const response = await fetch(`${API_URL}/uploads/sign`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ files }),
  });

  if (!response.ok) {
    throw new Error(await readJsonError(response));
  }

  const data = await response.json();
  if (!Array.isArray(data?.uploads)) {
    throw new Error("Malformed upload-url response");
  }
  return data.uploads;
};

/** PUT a single file directly to its signed GCS URL. */
export const uploadFileToSignedUrl = async (
  file: File,
  url: string,
  contentType: string
): Promise<void> => {
  const response = await fetch(url, {
    method: "PUT",
    headers: { "Content-Type": contentType },
    body: file,
  });

  if (!response.ok) {
    throw new Error(`Image upload failed (HTTP ${response.status})`);
  }
};

/**
 * Compress (gently) + upload the given files straight to GCS, returning the
 * pending object keys to submit with the report/cleanup. Order is preserved.
 */
export const uploadImagesDirect = async (
  files: File[],
  token: string
): Promise<string[]> => {
  if (!files.length) {
    return [];
  }

  const prepared = await Promise.all(
    files.map((file, index) => prepareImageForDirectUpload(file, index))
  );

  const signed = await requestUploadUrls(
    prepared.map((file) => ({ contentType: normalizeContentType(file.type) })),
    token
  );

  // Upload in small concurrent batches, preserving index alignment.
  for (let i = 0; i < prepared.length; i += MAX_CONCURRENT_UPLOADS) {
    const slice = prepared.slice(i, i + MAX_CONCURRENT_UPLOADS);
    await Promise.all(
      slice.map((file, offset) => {
        const upload = signed[i + offset];
        return uploadFileToSignedUrl(file, upload.url, upload.contentType);
      })
    );
  }

  return signed.map((upload) => upload.key);
};

/**
 * Map an upload error to a user-facing message, translating the sentinel
 * thrown for images the browser couldn't decode (e.g. unsupported HEIC).
 */
export const getUploadErrorMessage = (
  error: any,
  t: (key: string) => string
): string => {
  if (error?.message === "UNSUPPORTED_IMAGE") {
    return t("report.unsupportedImage");
  }
  return error?.message || t("report.unsupportedImage");
};
